import { graphql } from "gatsby";
import React from "react";
import { microCopy } from "@bmi/microcopies";
import {
  type MicroCopy as MicroCopyData,
  generateGetMicroCopy
} from "../components/MicroCopy";
import Page from "../components/Page";
import { SiteData, HomePageData } from "../components/Site";
import {
  FooterMainNavigationSiteMapSection,
  FooterSecondaryNavigationSiteMapSection,
  MenuNavigationSiteMapSection,
  MenuUtilitiesSiteMapSection
} from "../components/SitemapSection";
import type { CalculatorConfig } from "../components/pitched-roof-calculator/types";
import type { HouseType } from "../components/visualiser/Types";
import type { GeneralErrorData } from "./general-error";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { Visualiser } from "../components/Visualiser";

export type Props = {
  data: {
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
  pageContext: {
    variantCodeToPathMap?: Record<string, string>;
  };
};

const Sitemap = ({ data, pageContext }: Props) => {
  const siteData = data.payloadSite;
  const {
    footerMainNavigation,
    footerMainNavigationSiteMapTitle,
    footerSecondaryNavigation,
    footerSecondaryNavigationSiteMapTitle,
    menuNavigation,
    menuNavigationSiteMapTitle,
    menuUtilities,
    menuUtilitiesSiteMapTitle
  } = siteData;

  const getMicroCopy = generateGetMicroCopy(data.allPayloadMicroCopy.nodes);

  return (
    <Page
      title={getMicroCopy(microCopy.GLOBAL_SITEMAP)}
      pageData={{
        breadcrumbs: null,
        signupBlock: null,
        meta: null,
        path: null
      }}
      siteData={siteData}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
      variantCodeToPathMap={pageContext?.variantCodeToPathMap}
    >
      <MenuNavigationSiteMapSection
        menuNavigation={menuNavigation}
        menuNavigationSiteMapTitle={menuNavigationSiteMapTitle}
      />
      {menuUtilities?.length && menuUtilitiesSiteMapTitle ? (
        <MenuUtilitiesSiteMapSection
          menuUtilities={menuUtilities}
          menuUtilitiesSiteMapTitle={menuUtilitiesSiteMapTitle}
        />
      ) : undefined}
      {footerMainNavigation?.length && footerMainNavigationSiteMapTitle ? (
        <FooterMainNavigationSiteMapSection
          footerMainNavigation={footerMainNavigation}
          footerMainNavigationSiteMapTitle={footerMainNavigationSiteMapTitle}
        />
      ) : undefined}
      {footerSecondaryNavigation?.length &&
      footerSecondaryNavigationSiteMapTitle ? (
        <FooterSecondaryNavigationSiteMapSection
          footerSecondaryNavigation={footerSecondaryNavigation}
          footerSecondaryNavigationSiteMapTitle={
            footerSecondaryNavigationSiteMapTitle
          }
        />
      ) : undefined}
    </Page>
  );
};

export default Sitemap;

export const pageQuery = graphql`
  query SitemapPage {
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
